import "@/App.css";
import KioskGuard from "@/components/KioskGuard";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/AuthContext";
import Layout from "@/components/Layout";
import CrmLayout from "@/components/CrmLayout";
import Home from "@/pages/Home";
import Checkin from "@/pages/Checkin";
import TrackerSignup from "@/pages/TrackerSignup";
import Kiosk from "@/pages/Kiosk";
import InstallPrompt from "@/components/InstallPrompt";
import Ministry from "@/pages/Ministry";
import ToolPage from "@/pages/ToolPage";
import Demo from "@/pages/Demo";
import Social, { SocialJoin } from "@/pages/Social";
import SocialProfile from "@/pages/SocialProfile";
import KioskLaunch from "@/pages/KioskLaunch";
import PrayerPublic from "@/pages/prayer/PrayerPublic";
import PrayerAdmin from "@/pages/prayer/PrayerAdmin";
import Inquiry from "@/pages/Inquiry";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import About from "@/pages/About";
import Membership from "@/pages/Membership";import Pricing from "@/pages/Pricing";
import Dashboard from "@/pages/Dashboard";
import MyFamily from "@/pages/MyFamily";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Admin from "@/pages/Admin";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Counseling from "@/pages/Counseling";
import BeforeTheVows from "@/pages/BeforeTheVows";
import MarriageMentoring from "@/pages/MarriageMentoring";
import IndividualCounseling from "@/pages/IndividualCounseling";
import SeriesDetail from "@/pages/SeriesDetail";
import Library from "@/pages/Library";
import ReadWithMe from "@/pages/ReadWithMe";
import Commentary from "@/pages/Commentary";
import Resources from "@/pages/Resources";
import CoordinatorDashboard from "@/pages/CoordinatorDashboard";
import CrmLogin from "@/pages/crm/CrmLogin";
import CrmAccount from "@/pages/crm/CrmAccount";
import CrmHelp from "@/pages/crm/CrmHelp";
import CrmBilling from "@/pages/crm/CrmBilling";
import TeacherLayout from "@/components/TeacherLayout";
import TeacherLogin from "@/pages/teacher/TeacherLogin";
import TeacherDashboard from "@/pages/teacher/TeacherDashboard";
import TeacherGuestDetail from "@/pages/teacher/TeacherGuestDetail";
import AttendanceLayout from "@/components/AttendanceLayout";
import AttendanceLogin from "@/pages/attendance/AttendanceLogin";
import AttendanceDashboard from "@/pages/attendance/AttendanceDashboard";
import SectionLeaderHome from "@/pages/attendance/SectionLeaderHome";

function MainSite() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/ministry" element={<Ministry />} />
        <Route path="/tools/:slug" element={<ToolPage />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/social" element={<Social />} />
        <Route path="/social/join/:code" element={<SocialJoin />} />
        <Route path="/social/u/:userId" element={<SocialProfile />} />
        <Route path="/books" element={<Books />} />
        <Route path="/books/:id" element={<BookDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-family" element={<MyFamily />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogPost />} />
        <Route path="/read-with-me" element={<ReadWithMe />} />
        <Route path="/counseling" element={<Counseling />} />
        <Route path="/counseling/before-the-vows" element={<BeforeTheVows />} />
        <Route path="/counseling/marriage-mentoring" element={<MarriageMentoring />} />
        <Route path="/counseling/individual" element={<IndividualCounseling />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/dashboard/series/:slug" element={<SeriesDetail />} />
        <Route path="/dashboard/library" element={<Library />} />
        <Route path="/dashboard/commentary" element={<Commentary />} />
        <Route path="/coord" element={<Navigate to="/crm" replace />} />
        <Route path="/dashboard/devotionals" element={<Navigate to="/read-with-me" replace />} />
        <Route path="/dashboard/devotionals/:date" element={<Navigate to="/read-with-me" replace />} />
      </Routes>
    </Layout>
  );
}

function CrmSite() {
  return (
    <CrmLayout>
      <Routes>
        <Route path="login" element={<CrmLogin />} />
        <Route path="" element={<CoordinatorDashboard />} />
        <Route path="first-steps" element={<CoordinatorDashboard />} />
        <Route path="next-steps" element={<CoordinatorDashboard />} />
        <Route path="directory" element={<CoordinatorDashboard />} />
        <Route path="teachers" element={<CoordinatorDashboard />} />
        <Route path="team-activity" element={<CoordinatorDashboard />} />
        <Route path="team" element={<CoordinatorDashboard />} />
        <Route path="account" element={<CrmAccount />} />
        <Route path="billing" element={<CrmBilling />} />
        <Route path="help" element={<CrmHelp />} />
      </Routes>
    </CrmLayout>
  );
}

function TeacherSite() {
  return (
    <TeacherLayout>
      <Routes>
        <Route path="login" element={<TeacherLogin />} />
        <Route path="" element={<TeacherDashboard />} />
        <Route path="guests/:id" element={<TeacherGuestDetail />} />
      </Routes>
    </TeacherLayout>
  );
}

function AttendanceSite() {
  return (
    <AttendanceLayout>
      <Routes>
        <Route path="login" element={<AttendanceLogin />} />
        <Route path="" element={<AttendanceDashboard />} />
        <Route path="section" element={<SectionLeaderHome />} />
      </Routes>
    </AttendanceLayout>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/checkin/:code" element={<Checkin />} />
            <Route path="/tracker-signup/:code" element={<TrackerSignup />} />
            <Route path="/prayer/:code" element={<PrayerPublic />} />
            <Route path="/inquiry/:code" element={<Inquiry />} />
            <Route path="/kiosk/:code" element={<Kiosk />} />
            <Route path="/kiosk-launch" element={<KioskLaunch />} />
            <Route path="/prayer-admin" element={<PrayerAdmin />} />
            <Route path="/crm/*" element={<CrmSite />} />
            <Route path="/teacher/*" element={<TeacherSite />} />
            <Route path="/attendance/*" element={<AttendanceSite />} />
            <Route path="*" element={<MainSite />} />
          </Routes>
          <KioskGuard />
          <Toaster position="top-center" richColors />
      <InstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
